import React, { memo, useCallback } from 'react'
import tw from 'twin.macro'
import { Button } from 'src/components/shared/Button'
import {
  CustomerCollectedDataStatus,
  CustomerCollectedDataTypeExtended,
} from 'src/constants/customer/enums'
import { GSIcon } from 'src/components/shared/GSIcon'
import { ContractorStepDetailsCard } from 'src/components/contractorStepDetailsCard/ContractorStepDetailsCard'
import {
  ContractorStepDetailsCardStatus,
  ContractorStepDetailsCardUsageVariant,
} from 'src/components/contractorStepDetailsCard/constants'
import { mapMCStatusToCardStatus } from 'src/components/contractorStepDetailsCard/utils'

const complianceStepSlug = (stepType: CustomerCollectedDataTypeExtended) =>
  String(stepType)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '') || 'step'

export interface MCStepParams {
  stepStatus: CustomerCollectedDataStatus
  stepType: CustomerCollectedDataTypeExtended
  expiresAt?: string
  daysToExpire?: number | string | null
  onResubmitInfo?: () => void
  onChangeExpirationDate?: () => void
  onViewDetails?: () => void
}

interface Props {
  stepStatus: CustomerCollectedDataStatus
  stepType: CustomerCollectedDataTypeExtended
  expiresAt?: string
  daysToExpire?: number | string | null
  onChangeExpirationDate?: (stepType: CustomerCollectedDataTypeExtended) => void
  onViewDetails?: (stepType: CustomerCollectedDataTypeExtended) => void
  onResubmitInfo?: (stepType: CustomerCollectedDataTypeExtended) => void
}

const MCStepItemComponent = ({
  stepStatus,
  stepType,
  expiresAt,
  daysToExpire,
  onChangeExpirationDate,
  onViewDetails,
  onResubmitInfo,
}: Props) => {
  const handleChangeExpirationDate = useCallback(() => {
    onChangeExpirationDate?.(stepType)
  }, [stepType, onChangeExpirationDate])

  const handleViewDetails = useCallback(() => {
    onViewDetails?.(stepType)
  }, [stepType, onViewDetails])

  const handleResubmitInfo = useCallback(() => {
    onResubmitInfo?.(stepType)
  }, [stepType, onResubmitInfo])

  const baseCardStatus = mapMCStatusToCardStatus(stepStatus)
  const isExpired = stepStatus === CustomerCollectedDataStatus.Expired
  const expiresSoon = !isExpired && daysToExpire != null && Number(daysToExpire) <= 30
  let cardStatus = expiresSoon ? ContractorStepDetailsCardStatus.ExpiringSoon : baseCardStatus

  if (isExpired) {
    cardStatus = ContractorStepDetailsCardStatus.Expired
  }

  const stepSlug = complianceStepSlug(stepType)

  const submissionActions =
    onViewDetails || onChangeExpirationDate ? (
      <div css={tw`flex flex-row items-center gap-2`}>
        {onChangeExpirationDate && (
          <Button
            iconOnly
            rightIcon={<GSIcon iconName="gs_calendar_edit" />}
            onClick={handleChangeExpirationDate}
            size="small"
            color="grey"
            data-testid={`customer-account-compliance-step-${stepSlug}-change-expiration-btn`}
          />
        )}
        {onViewDetails && (
          <Button
            iconOnly
            rightIcon={<GSIcon iconName="gs_eye_open" />}
            onClick={handleViewDetails}
            size="small"
            color="white"
            data-testid={`customer-account-compliance-step-${stepSlug}-view-details-btn`}
          />
        )}
      </div>
    ) : undefined

  return (
    <ContractorStepDetailsCard
      variant={ContractorStepDetailsCardUsageVariant.MasterContractor}
      stepType={stepType}
      status={cardStatus}
      expiresAt={expiresAt}
      actions={submissionActions}
      children={
        onResubmitInfo ? (
          <Button
            rightIcon={
              stepStatus !== CustomerCollectedDataStatus.Created ? (
                <GSIcon iconName="gs_arrows_repeat_right_left" />
              ) : undefined
            }
            onClick={handleResubmitInfo}
            css={tw`w-full`}
            size="small"
            color={stepStatus !== CustomerCollectedDataStatus.Created ? 'grey' : 'primary'}
            data-testid={`customer-account-compliance-step-${stepSlug}-primary-btn`}
          >
            {stepStatus !== CustomerCollectedDataStatus.Created ? 'Resubmit Info' : 'Submit Info'}
          </Button>
        ) : undefined
      }
    />
  )
}

export const MCStepItem = memo(MCStepItemComponent)
